package ru.it_mozaika.sender.js.application.context

import com.github.doyaaaaaken.kotlincsv.dsl.csvReader
import io.ktor.client.call.*
import js.core.jso
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.async
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import org.khronos.webgl.ArrayBuffer
import react.*
import ru.it_mozaika.sender.common.EMAIL_REGEX
import ru.it_mozaika.sender.common.IN_PRODUCTION
import ru.it_mozaika.sender.common.PHONE_REGEX
import ru.it_mozaika.sender.common.models.Property
import ru.it_mozaika.sender.common.models.PropertyType
import ru.it_mozaika.sender.common.models.SubscriberData
import ru.it_mozaika.sender.common.models.SubscriberDataProperty
import ru.it_mozaika.sender.js.application.Http
import ru.it_mozaika.sender.js.application.components.subscribers.confirmDialog
import ru.it_mozaika.sender.js.application.excelUploadRequest
import ru.it_mozaika.sender.js.application.localToInstant
import web.file.File
import web.file.FileReader

external interface AddSubscribersStepperInterface{
    var activeStep: Int
    var emailIsIndicated: Boolean
    var setFile: (File?) -> Unit
    var importRequest: () -> Unit
    var fileData: List<Map<String, String>>
    var changeStep: (Int) -> Unit
    var setEmailIsIndicated: (Boolean) -> Unit
    var file: File?
    var deleteFile: () -> Unit
    var setEmailVerified: (Boolean) -> Unit
    var emailVerified: Boolean
    var columnIdentifier: Map<String, Property>
    var properties: List<Property>
    var propertiesInUse: List<Property>
    var columns: List<String>
    var setColumnIdentifier: (String, Property) -> Unit
    var deleteFromColumnIdentifier: (String) -> Unit
    var subscribers: List<SubscriberData>
    var clearData: () -> Unit
}
val AddSubscribersStepperContext = createContext<AddSubscribersStepperInterface>(
    jso {
        activeStep = 0
        emailIsIndicated = false
        importRequest = {}
        fileData = listOf()
        changeStep = {}
        setEmailIsIndicated = {}
        deleteFile = {}
        setEmailVerified = {}
        emailVerified = false
        columnIdentifier = mapOf()
        properties = listOf()
        propertiesInUse = listOf()
        columns = listOf()
        setColumnIdentifier = {_, _ -> }
        deleteFromColumnIdentifier = {}
        subscribers = listOf()
        clearData = {}
    }
)

val AddSubscribersStepperProvider = FC<PropsWithChildren> { props ->
    val backdrop = useContext(BackdropContext)
    var activeStep by useState(0)
    val storeContext = useStore()
    var fileReadResult by useState<List<Map<String, String>>>(listOf())
    var emailIsIndicated by useState(false)
    var file by useState<File>()
    val requestResponse = useContext(RequestResultContext)
    var emailVerified by useState(false)
    val (errorsList, setErrorsList) = useState<List<String>>(listOf())
    val (showConfirmDialog, setShowConfirmDialog) = useState(false)
    val (columnIdentifier, setColumnIdentifier) = useState<Map<String, Property>>(mapOf())
    val (propertiesInUse, setPropertiesInUse) = useState<List<Property>>(listOf())
    var subscribers by useState(listOf<SubscriberData>())
    val columns = useMemo(fileReadResult) {
        if (fileReadResult.isNotEmpty()) fileReadResult.first().map { it.key } else listOf()
    }
    val (properties, setProperties) = useState(
        listOf(
            Property(id = "Email", store = storeContext.currentStoreId!!, name = "Email", variable = "Email"),
            Property(id = "Phone", store = storeContext.currentStoreId!!, name = "Телефон", variable = "Phone")
        )
    )
    fun addSubscriber(request: List<SubscriberData>) {
        backdrop.show()
        Http.post(
            path = "/subscribers/add",
            body = request,
        ) { response ->
            requestResponse.handleRequestRespond(response)
            backdrop.hide()
        }
    }

    fun loadProperties() {
        Http.get("/subscribers/properties", mapOf("store" to storeContext.currentStoreId!!)) { response ->
            val newList = response.body<List<Property>>()
            setProperties {
                it.plus(newList)
            }
        }
    }
    AddSubscribersStepperContext.Provider {
        value = jso {
            clearData = {
                file = null
                emailVerified = false
                subscribers = listOf()
            }
            this.subscribers = useMemo(subscribers) { subscribers }
            this.fileData = useMemo(fileReadResult) { fileReadResult }
            this.deleteFromColumnIdentifier = { headerName ->
                setColumnIdentifier {
                    it.minus(headerName)
                }
            }
            this.setColumnIdentifier = { headerName, selectedProperty ->
                setColumnIdentifier { current ->
                    val newValue =
                        current.plus(mapOf(headerName to selectedProperty))
                    newValue
                }
            }
            this.columns = useMemo(columns) { columns }
            this.propertiesInUse = useMemo(propertiesInUse) { propertiesInUse }
            this.properties = useMemo(properties) { properties }
            this.columnIdentifier = useMemo(columnIdentifier) {
                columnIdentifier
            }
            this.emailVerified = useMemo(emailVerified) { emailVerified }
            this.file = useMemo(file) { file }
            setEmailVerified = {
                emailVerified = it
            }
            this.deleteFile = {
                file = null
            }
            this.setEmailIsIndicated = {
                emailIsIndicated = it
            }
            this.activeStep = useMemo(activeStep) { activeStep }
            this.changeStep = {
                activeStep = it
            }
            this.emailIsIndicated = useMemo(emailIsIndicated) { emailIsIndicated }
            this.setFile = {
                file = it
            }
            this.importRequest = {
                if (subscribers.isNotEmpty() && errorsList.isEmpty()) {
                    addSubscriber(subscribers)
                } else {
                    setShowConfirmDialog(true)
                }
            }
        }
        confirmDialog {
            this.onClose = {
                setShowConfirmDialog(false)
            }
            this.originalCount = fileReadResult.size
            this.errors = errorsList
            this.requestWithErrors = subscribers
            this.isOpen = showConfirmDialog
            this.onDownloadClick = {
                addSubscriber(subscribers)
                setShowConfirmDialog(false)
            }
        }
        +props.children
    }
    useEffect(file) {
        if (file != null) {
            when (file?.type) {
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" -> {
                    val reader = FileReader()
                    reader.readAsArrayBuffer(file!!)
                    reader.onload = {
                        val buffer = reader.result as ArrayBuffer
                        backdrop.show()
                        excelUploadRequest(
                            path = "/subscribers/add/excel",
                            file = file!!,
                            buffer = buffer,
                            query = mapOf(),
                            response = { response ->
                                CoroutineScope(Dispatchers.Default).launch {
                                    val result = async {
                                        response.body<List<Map<String, String>>>()
                                    }.await()
                                    fileReadResult = result
                                    activeStep = 1
                                    backdrop.hide()
                                }
                            }
                        )
                    }
                }
                else -> {
                    CoroutineScope(Dispatchers.Default).launch {
                        backdrop.show()
                        val reader = FileReader()
                        file.let {
                            reader.readAsText(it!!)
                        }
                        reader.onload = {
                            val string = reader.result as String
                            val result = csvReader().readAllWithHeader(string)
                            fileReadResult = result
                            backdrop.hide()
                            activeStep = 1
                        }
                    }
                }
            }
        } else {
            fileReadResult = listOf()
            setErrorsList(listOf())
        }
    }
    useEffect(columnIdentifier, fileReadResult) {
        val isIndicated = columnIdentifier.filterValues { it.id == "Email" }.isNotEmpty()
        emailIsIndicated = isIndicated
        setPropertiesInUse {
            properties.filter { property ->
                property in columnIdentifier.map { it.value }
            }
        }
    }
    useEffect(properties, fileReadResult) {
        val newMap = mutableMapOf<String, Property>()
        columns.forEach { column ->
            val property = properties.find { it.name.lowercase() == column.lowercase() }
            if (property != null) {
                newMap[column] = property
            }
        }
        setColumnIdentifier(newMap)
    }
    useEffect(fileReadResult, columnIdentifier, emailVerified) {
        val errors = mutableListOf<String>()
        val requestProcessing = if (fileReadResult.isNotEmpty()) {
            fileReadResult.mapIndexedNotNull { index, map ->
                val emailKey = columnIdentifier.filterValues { it.id == "Email" }.keys.firstOrNull()
                val phoneKey = columnIdentifier.filterValues { it.id == "Phone" }
                val phone = if (phoneKey.isNotEmpty()) map[phoneKey.keys.first()] else null
                val email = map[emailKey]
                if (map[emailKey] != null) {
                    if (!email?.matches(EMAIL_REGEX)!!) {
                        errors += "Некорректно заполнен Email - $email в строке ${index + 2}."
                        return@mapIndexedNotNull null
                    }
                    val propertiesList = map.entries.mapNotNull { (header, value) ->
                        if (value.isNotEmpty() && value.isNotBlank()) {
                            val property = columnIdentifier[header]
                            if (property?.id == "Phone") {
                                if (!value.matches(PHONE_REGEX)) {
                                    errors += "Некорректно заполнен Телефон $phone в строке ${index + 2}"
                                }
                            }
                            if (property != null && property.id != "Email" && property.id != "Phone") {
                                try {
                                    when (property.type) {
                                        PropertyType.String -> SubscriberDataProperty.StringProperty(
                                            propertyId = property.id,
                                            values = listOf(value)
                                        )

                                        PropertyType.Int -> SubscriberDataProperty.IntProperty(
                                            propertyId = property.id,
                                            values = listOf(value.toInt())
                                        )

                                        PropertyType.Float -> SubscriberDataProperty.FloatProperty(
                                            propertyId = property.id,
                                            values = listOf(value.toFloat())
                                        )

                                        PropertyType.Date -> SubscriberDataProperty.DateProperty(
                                            propertyId = property.id,
                                            values = listOf(
                                                value.localToInstant()
                                            )
                                        )

                                        PropertyType.Bool -> SubscriberDataProperty.BoolProperty(
                                            propertyId = property.id,
                                            values = listOf(value.toBooleanStrict())
                                        )
                                    }
                                } catch (e: Exception) {
                                    errors += "Указано некорректное значение \"$value\" в поле \"$header\" в строке ${index + 2}"
                                    null
                                }
                            } else {
                                null
                            }
                        } else {
                            null
                        }
                    }
                    SubscriberData(
                        id = "",
                        email = map[emailKey] ?: throw (NullPointerException()),
                        store = storeContext.currentStoreId!!,
                        subscriptions = listOf(),
                        emailVerified = emailVerified,
                        properties = propertiesList.filter { it.values.isNotEmpty() },
                        regDate = Clock.System.now(),
                        phone = phone

                    )
                } else {
                    errors += "Не удалось обнаружить поле Email"
                    null
                }
            }
        } else listOf()
        setErrorsList(errors)
        subscribers = requestProcessing
    }
    useEffect(storeContext.currentStoreId) {
        loadProperties()
    }
}
